import React, { Fragment, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { saveAs } from 'file-saver';

import { useTranslation } from 'react-i18next';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Skeleton from '@material-ui/lab/Skeleton';
import SaveIcon from '@material-ui/icons/SaveAltSharp';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

import pushToGTM, { EXPORT_SCHEMATIC_GA4_TAG } from '../../utils/analytics/gtm';
import { DIMENSIONS } from '../../styles/constants';
import { useStyles } from './styles';
import { SCHEMATICS } from '../Schematic/constants/schematics';
import Schematic from '../Schematic';
import Paper from '../Paper';

const DetailedSchematic = ({ withTitle, page }) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const designId = useSelector(state => state.selectedDesign.myDesign.id);
	const rankingParameters = useSelector(state => state.selectedDesign.detail.rankingParameters);
	const icPartNumber = useSelector(state => state.selectedDesign.icPartNumber);
	const topology = useSelector(state => state.selectedDesign.topology);
	const progressActive = useSelector(state => state.tabbar.progressActive);

	function downloadSchematic() {
		const blob = new Blob([document.getElementById('schematic_el').outerHTML], {
			type: 'image/svg+xml;charset=utf-8'
		});
		saveAs(blob, 'WebDesigner+ ' + designId + ' - ' + icPartNumber + ' schematic.svg');
	}

	const transformWrapperRef = useRef();

	const zoomIn = () => {
		transformWrapperRef.current.zoomIn();
	};

	const zoomOut = () => {
		transformWrapperRef.current.zoomOut();
	};

	return (
		<Paper padding={[1, 2, 2, 2]}>
			<Grid container direction="column">
				<Grid
					item
					container
					direction="row"
					alignItems="center"
					justify={withTitle ? 'space-between' : 'flex-end'}
					className={classes.header}>
					{withTitle &&
						(progressActive ? (
							<Skeleton width={DIMENSIONS.SKELETON_BUTTON_WIDTH} height={DIMENSIONS.SKELETON_TITLE_HEIGHT} />
						) : (
							<Grid item>
								<Typography variant="h6">{t('components.summary.schematic')}</Typography>
							</Grid>
						))}

					<Grid item>
						{progressActive ? (
							<Skeleton
								variant="circle"
								width={DIMENSIONS.SKELETON_BUTTON_HEIGHT}
								height={DIMENSIONS.SKELETON_BUTTON_HEIGHT}
							/>
						) : (
							<>
								<Tooltip title={t('components.detailedSchematic.zoomIn')}>
									<IconButton
										aria-label="Zoom In"
										disabled={progressActive}
										onClick={zoomIn}
										className={classes.focusableIconButton}>
										<ZoomInIcon />
									</IconButton>
								</Tooltip>
								<Tooltip title={t('components.detailedSchematic.zoomOut')}>
									<IconButton
										aria-label="Zoom Out"
										disabled={progressActive}
										onClick={zoomOut}
										className={classes.focusableIconButton}>
										<ZoomOutIcon />
									</IconButton>
								</Tooltip>
								<Tooltip title={t('components.design.downloadSvg')}>
									<IconButton
										aria-label="Download SVG"
										disabled={progressActive}
										onClick={() => {
											pushToGTM(EXPORT_SCHEMATIC_GA4_TAG);
											downloadSchematic();
										}}
										className={classes.focusableIconButton}>
										<SaveIcon />
									</IconButton>
								</Tooltip>
							</>
						)}
					</Grid>
				</Grid>

				{progressActive ? (
					<Skeleton variant="rect" height={400} className={classes.schematicSkeleton} />
				) : (
					<Fragment>
						<Grid item container justify="center" alignItems="center">
							<Grid item className={classes.content}>
								<TransformWrapper ref={transformWrapperRef}>
									<TransformComponent>
										<Schematic
											icPartNumber={icPartNumber}
											topology={topology}
											topologyCategory={rankingParameters.topologyCategory}
											schematic={rankingParameters.schematic}
											page={page}
										/>
									</TransformComponent>
								</TransformWrapper>
							</Grid>
						</Grid>

						{SCHEMATICS[rankingParameters.schematic] && SCHEMATICS[rankingParameters.schematic].note ? (
							<Grid item>
								<Typography variant="caption" color="textSecondary">
									{'*' + SCHEMATICS[rankingParameters.schematic].note}
								</Typography>
							</Grid>
						) : null}
					</Fragment>
				)}
			</Grid>
		</Paper>
	);
};

DetailedSchematic.propTypes = {
	withTitle: PropTypes.bool,
	page: PropTypes.bool.isRequired
};

export default DetailedSchematic;
